import { apiClient } from '../config';
import { endpoints } from '../endpoints';

/**
 * Journal service for handling journal operations
 */
export const journalService = {
  /**
   * Get all journals with optional filtering
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=10] - Results per page
   * @param {string} [params.category] - Filter by category ID
   * @param {string} [params.q] - Search query for journal name/description
   * @returns {Promise<Object>} - Promise resolving to paginated journals
   */
  getAllJournals: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.category) queryParams.append('category', params.category);
    if (params.q) queryParams.append('q', params.q);

    const url = `${endpoints.journals.getAll}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Get journal by ID
   * @param {string} id - Journal ID
   * @returns {Promise<Object>} - Promise resolving to journal data
   */
  getJournalById: async (id) => {
    const response = await apiClient.get(endpoints.journals.getById(id));
    return response.data;
  },

  /**
   * Get journals by category
   * @param {string} categoryId - Category ID
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=10] - Results per page
   * @returns {Promise<Object>} - Promise resolving to paginated journals
   */
  getJournalsByCategory: async (categoryId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const url = `${endpoints.journals.getByCategory(categoryId)}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Create a new journal
   * @param {Object} journalData - Journal data
   * @param {string} journalData.title - Journal title
   * @param {string} journalData.description - Journal description
   * @param {string} [journalData.aimAndScope] - Journal aim and scope
   * @param {string} [journalData.issn] - Journal ISSN
   * @param {string} [journalData.eissn] - Journal eISSN
   * @param {string[]} journalData.categories - Array of category IDs
   * @returns {Promise<Object>} - Promise resolving to created journal
   */
  createJournal: async (journalData) => {
    // Convert categories to categoryIds as required by the API
    const payload = {
      ...journalData,
      categoryIds: journalData.categories,
    };
    // Remove the categories field as it's not needed in the API
    delete payload.categories;

    const response = await apiClient.post(endpoints.journals.create, payload);
    return response.data;
  },

  /**
   * Update an existing journal
   * @param {string} id - Journal ID
   * @param {Object} journalData - Journal data to update
   * @param {string} [journalData.title] - Journal title
   * @param {string} [journalData.description] - Journal description
   * @param {string} [journalData.aimAndScope] - Journal aim and scope
   * @param {string} [journalData.issn] - Journal ISSN
   * @param {string} [journalData.eissn] - Journal eISSN
   * @param {string[]} [journalData.categories] - Array of category IDs
   * @returns {Promise<Object>} - Promise resolving to updated journal
   */
  updateJournal: async (id, journalData) => {
    // Convert categories to categoryIds as required by the API
    const payload = { ...journalData };

    if (journalData.categories) {
      payload.categoryIds = journalData.categories;
      delete payload.categories;
    }

    const response = await apiClient.put(endpoints.journals.update(id), payload);
    return response.data;
  },

  /**
   * Delete a journal
   * @param {string} id - Journal ID
   * @returns {Promise<Object>} - Promise resolving to success message
   */
  deleteJournal: async (id) => {
    const response = await apiClient.delete(endpoints.journals.delete(id));
    return response.data;
  },

  /**
   * Get journals by editor (for chief editor)
   * @returns {Promise<Array>} - Promise resolving to array of journals
   */
  getJournalsByEditor: async () => {
    const response = await apiClient.get(endpoints.journals.getByEditor);
    return response.data;
  },

  /**
   * Approve or reject a journal (admin only)
   * @param {string} id - Journal ID
   * @param {Object} approvalData - Approval data
   * @param {string} approvalData.approvalStatus - Approval status (APPROVED, REJECTED, PENDING)
   * @param {string} [approvalData.rejectionReason] - Reason for rejection (required if status is REJECTED)
   * @returns {Promise<Object>} - Promise resolving to updated journal
   */
  approveJournal: async (id, approvalData) => {
    const response = await apiClient.put(endpoints.journals.approve(id), approvalData);
    return response.data;
  },

  /**
   * Get pending journal approvals (admin only)
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 10)
   * @returns {Promise<Object>} - Promise resolving to paginated journals data
   */
  getPendingApprovals: async (page = 1, limit = 10) => {
    const response = await apiClient.get(endpoints.journals.getPendingApprovals, {
      params: { page, limit }
    });
    return response.data;
  },

  /**
   * Add a chief editor to a journal
   * @param {string} journalId - Journal ID
   * @param {string} chiefEditorId - User ID of the chief editor to add
   * @returns {Promise<Object>} - Promise resolving to the added chief editor data
   */
  addChiefEditor: async (journalId, chiefEditorId) => {
    const response = await apiClient.post(endpoints.journals.addChiefEditor(journalId), { chiefEditorId });
    return response.data;
  },

  /**
   * Change the main chief editor of a journal
   * @param {string} journalId - Journal ID
   * @param {string} chiefEditorId - User ID of the chief editor to make the main chief editor
   * @returns {Promise<Object>} - Promise resolving to the updated chief editor data
   */
  changeMainChiefEditor: async (journalId, chiefEditorId) => {
    const response = await apiClient.put(endpoints.journals.changeMainChiefEditor(journalId), { chiefEditorId });
    return response.data;
  },

  /**
   * Remove a chief editor from a journal (Admin only)
   * @param {string} journalId - Journal ID
   * @param {string} chiefEditorId - User ID of the chief editor to remove
   * @returns {Promise<Object>} - Promise resolving to success message
   */
  removeChiefEditor: async (journalId, chiefEditorId) => {
    const response = await apiClient.delete(endpoints.journals.removeChiefEditor(journalId, chiefEditorId));
    return response.data;
  },

  /**
   * Upload cover photo for a journal
   * @param {string} journalId - Journal ID
   * @param {FormData} formData - FormData containing the cover photo file
   * @returns {Promise<Object>} - Promise resolving to the updated journal with cover photo URL
   */
  uploadCoverPhoto: async (journalId, formData) => {
    const response = await apiClient.post(
      endpoints.journals.uploadCoverPhoto(journalId), 
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Delete cover photo for a journal
   * @param {string} journalId - Journal ID
   * @returns {Promise<Object>} - Promise resolving to success message
   */
  deleteCoverPhoto: async (journalId) => {
    const response = await apiClient.delete(endpoints.journals.deleteCoverPhoto(journalId));
    return response.data;
  }
};
